import React from 'react';
import { useTranslation } from 'react-i18next';
import type { WaybillBlocker } from '@hero/schema';
import { getApiErrorMessage } from '@hero/api';
import { cn } from '@hero/web-lib';
import { AlertCircleIcon, CircleCheckIcon } from '@hero/web-icons';

export interface WaybillEntityCardProps {
  icon: React.FC<{ className?: string }>;
  /** "Driver" / "Truck". */
  title: string;
  /** Who or what is assigned — a name, a plate. */
  name?: React.ReactNode;
  /** Second identifying line — a phone number, a vehicle type. */
  detail?: React.ReactNode;
  /** What Bayan rejects about this record, straight from the backend. */
  blockers: WaybillBlocker[];
  /** Shown in place of `name` when nothing is assigned. */
  emptyLabel: string;
  onOpen: () => void;
}

/**
 * The assigned driver or truck, plus whatever Bayan objects to about it.
 *
 * ## Why a card that opens a form instead of the fields inline
 *
 * The earlier version of this drawer re-implemented a subset of the driver and
 * vehicle forms — an ID input here, a plate input there. That meant two places
 * that could write a plate, each with its own validation, and a user who fixed
 * the plate here still saw the old value in the truck's own form. The card
 * shows the record and hands the editing to the form that owns it.
 *
 * ## Why the whole card is the target
 *
 * Someone about to register a trip with the government wants to see which
 * driver and which truck are going on the document before they press Issue —
 * not only when something is wrong with them. So the card is always present and
 * always opens the record, and the blocker list is an additional state rather
 * than the reason the card exists.
 */
export const WaybillEntityCard: React.FC<WaybillEntityCardProps> = ({
  icon: Icon,
  title,
  name,
  detail,
  blockers,
  emptyLabel,
  onOpen,
}) => {
  const { t } = useTranslation(['m-apps']);
  const hasBlockers = blockers.length > 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'w-full rounded-xl border p-3 text-start transition-colors',
        hasBlockers
          ? 'border-amber-300 bg-amber-50/60 hover:bg-amber-50'
          : 'border-gray-200 bg-white hover:bg-gray-50',
      )}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg',
            hasBlockers ? 'bg-amber-100' : 'bg-gray-100',
          )}>
          <Icon className={cn('size-5', hasBlockers ? 'text-amber-700' : 'text-gray-500')} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[12px] uppercase leading-[18px] tracking-wide text-gray-500">
            {title}
          </p>
          <p className="truncate text-[14px] font-[600] leading-[22px] text-gray-900">
            {name || <span className="font-[400] text-gray-400">{emptyLabel}</span>}
          </p>
          {detail ? (
            <p className="truncate text-[12px] leading-[18px] text-gray-500" dir="auto">
              {detail}
            </p>
          ) : null}
        </div>

        {hasBlockers ? (
          <span className="shrink-0 text-[12px] font-[600] leading-[18px] text-amber-700 underline">
            {t('m-apps:waybill.form.entity.fix')}
          </span>
        ) : (
          <CircleCheckIcon className="size-5 shrink-0 text-emerald-600" />
        )}
      </div>

      {hasBlockers ? (
        <ul className="mt-3 space-y-1 border-t border-amber-200 pt-2.5">
          {blockers.map(blocker => (
            <li key={blocker.code} className="flex items-start gap-2">
              <AlertCircleIcon className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
              <span className="text-[13px] leading-[20px] text-amber-900">
                {getApiErrorMessage(blocker.code) ?? blocker.message}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </button>
  );
};
